/**
 * NotesSection Component
 * General notes, issues, and tomorrow's plan for daily logs
 * BuilderBel Construction Project Management System
 */

import { AlertCircle, Calendar, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { TextArea } from '../../forms';

interface NotesSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const NotesSection: React.FC<NotesSectionProps> = ({
  control,
  errors,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  // Watch notes for summary
  const generalNotes = useWatch({ control, name: 'generalNotes' }) as string | undefined;
  const issuesAndConcerns = useWatch({ control, name: 'issuesAndConcerns' }) as string | undefined;
  const tomorrowPlan = useWatch({ control, name: 'tomorrowPlan' }) as string | undefined;

  const hasContent = !!(generalNotes || issuesAndConcerns || tomorrowPlan);
  const hasIssues = !!issuesAndConcerns;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <FileText className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Notes & Plans</span>
        </div>
        <div className="flex items-center space-x-4">
          {/* Summary indicators */}
          <div className="flex items-center space-x-2">
            {hasContent && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                Has notes
              </span>
            )}
            {hasIssues && (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded flex items-center">
                <AlertCircle className="h-3 w-3 mr-1" />
                Issues reported
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-6 bg-white">
          {/* General Notes */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <FileText className="h-4 w-4 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">General Notes</label>
            </div>
            <Controller
              name="generalNotes"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={errors.generalNotes?.message as string | undefined}
                  disabled={disabled}
                  rows={4}
                  placeholder="Document general observations, accomplishments, and noteworthy events from today..."
                />
              )}
            />
            <p className="text-xs text-gray-400">
              Summarize the day's activities, progress made, and any general observations.
            </p>
          </div>

          {/* Issues and Concerns */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <label className="text-sm font-medium text-gray-700">Issues & Concerns</label>
            </div>
            <Controller
              name="issuesAndConcerns"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={errors.issuesAndConcerns?.message as string | undefined}
                  disabled={disabled}
                  rows={4}
                  placeholder="Document any issues, concerns, or problems encountered today that need attention..."
                  className={field.value ? 'border-red-200 bg-red-50/30' : ''}
                />
              )}
            />
            <p className="text-xs text-gray-400">
              List problems requiring attention: safety concerns, quality issues, schedule impacts,
              resource constraints, or coordination problems.
            </p>
          </div>

          {/* Tomorrow's Plan */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              <label className="text-sm font-medium text-gray-700">Tomorrow's Plan</label>
            </div>
            <Controller
              name="tomorrowPlan"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={errors.tomorrowPlan?.message as string | undefined}
                  disabled={disabled}
                  rows={4}
                  placeholder="Outline planned activities, expected deliveries, scheduled inspections, and goals for tomorrow..."
                  className={field.value ? 'border-blue-200 bg-blue-50/30' : ''}
                />
              )}
            />
            <p className="text-xs text-gray-400">
              Plan for the next work day: scheduled activities, expected deliveries, inspections,
              crew assignments, and priorities.
            </p>
          </div>

          {/* Tips */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Tips for Effective Daily Notes
            </h4>
            <ul className="text-xs text-gray-500 space-y-1.5">
              <li className="flex items-start space-x-2">
                <span className="text-brand-600 font-bold">1.</span>
                <span>Be specific about locations, quantities, and time frames</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-brand-600 font-bold">2.</span>
                <span>Document any verbal instructions or decisions from the owner</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-brand-600 font-bold">3.</span>
                <span>Note any work that deviates from plans or specifications</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-brand-600 font-bold">4.</span>
                <span>Record conditions that may affect future work or claims</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-brand-600 font-bold">5.</span>
                <span>Include reference numbers for related documents (RFIs, COs, etc.)</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesSection;
